import React, { useState, useEffect } from 'react';
const API_URL = process.env.REACT_APP_API_URL;

// Helper to get valid admin access token, refresh if needed
async function getAdminAccessToken() {
  let accessToken = localStorage.getItem('gogig_admin_access');
  const refreshToken = localStorage.getItem('gogig_admin_refresh');
  if (accessToken) {
    try {
      const res = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (res.status !== 401) return accessToken;
    } catch {}
  }
  if (refreshToken) {
    try {
      const res = await fetch(`${API_URL}/api/admin/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });
      const data = await res.json();
      if (data.success && data.accessToken) {
        localStorage.setItem('gogig_admin_access', data.accessToken);
        return data.accessToken;
      }
    } catch {}
  }
  localStorage.removeItem('gogig_admin_access');
  localStorage.removeItem('gogig_admin_refresh');
  window.location.href = '/admin';
  return null;
}

async function adminFetch(url, options = {}) {
  const token = await getAdminAccessToken();
  if (!token) throw new Error('Not authenticated');
  const headers = { ...(options.headers || {}), Authorization: `Bearer ${token}` };
  return fetch(url, { ...options, headers });
}

function AdminSPOCReport({ apiBase, getToken, fetch: fetchProp }) {
  const getTokenFn = getToken || getAdminAccessToken;
  const fetchFn = fetchProp || adminFetch;
  const reportUrl = apiBase ? `${API_URL}/${apiBase}/spoc-report` : `${API_URL}/api/admin/spoc-report`;
  const [report, setReport] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedSpocs, setExpandedSpocs] = useState(new Set());

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchFn(reportUrl);
      const data = await res.json();
      if (data.success) {
        setReport(data.report);
        setSummary(data.summary);
      } else {
        setError(data.message || 'Failed to fetch report');
      }
    } catch (err) {
      setError('Failed to fetch report');
      console.error(err);
    }
    setLoading(false);
  };

  const toggleSpoc = (spocId) => {
    const newExpanded = new Set(expandedSpocs);
    if (newExpanded.has(spocId)) {
      newExpanded.delete(spocId);
    } else {
      newExpanded.add(spocId);
    }
    setExpandedSpocs(newExpanded);
  };

  const handleExportCSV = () => {
    if (!report.length) return;

    let csv = 'SPOC Name,SPOC Email,Company Name,Establishment Code,Registered,Unregistered,Active,Inactive,Total\n';

    report.forEach(spocData => {
      if (spocData.companies.length === 0) {
        csv += `"${spocData.spoc_name}","${spocData.spoc_email}",No Companies Assigned,,,,,,\n`;
      } else {
        spocData.companies.forEach(company => {
          csv += `"${spocData.spoc_name}","${spocData.spoc_email}","${company.company_name}","${company.establishment_code || ''}",${company.registered},${company.unregistered},${company.active},${company.inactive},${company.total}\n`;
        });
      }
    });

    // Add summary row
    if (summary) {
      csv += `\nSUMMARY,,,,${summary.total_registered},${summary.total_unregistered},${summary.total_active},${summary.total_inactive},${summary.grand_total}\n`;
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `spoc_report_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontWeight: 800, fontSize: 28, color: '#5a4ff3', margin: 0 }}>
          SPOC Report - Company-wise Employee Statistics
        </h2>
        <button 
          className="gogig-btn" 
          onClick={handleExportCSV} 
          disabled={loading || !report.length}
          style={{ padding: '10px 20px' }}
        >
          Export CSV
        </button>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 48, color: '#666' }}>
          Loading report...
        </div>
      )}

      {error && (
        <div style={{ 
          background: '#fee', 
          color: '#c33', 
          padding: 12, 
          borderRadius: 8, 
          marginBottom: 18 
        }}>
          {error}
        </div>
      )}

      {summary && !loading && (
        <div style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 12,
          padding: 24,
          marginBottom: 24,
          color: 'white',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: 20, fontWeight: 700 }}>Summary</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            <div>
              <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 4 }}>Total Registered</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{summary.total_registered}</div>
            </div>
            <div>
              <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 4 }}>Total Unregistered</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{summary.total_unregistered}</div>
            </div>
            <div>
              <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 4 }}>Total Active</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{summary.total_active}</div>
            </div>
            <div>
              <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 4 }}>Total Inactive</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{summary.total_inactive}</div>
            </div>
            <div>
              <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 4 }}>Grand Total</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{summary.grand_total}</div>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && report.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: 48, 
          color: '#888',
          background: '#f9fafb',
          borderRadius: 12
        }}>
          No SPOC data available.
        </div>
      )}

      {!loading && !error && report.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {report.map((spocData) => {
            const isExpanded = expandedSpocs.has(spocData.spoc_id);
            const totalCompanies = spocData.companies.length;
            const spocTotal = spocData.companies.reduce((acc, c) => ({
              registered: acc.registered + c.registered,
              unregistered: acc.unregistered + c.unregistered,
              active: acc.active + c.active,
              inactive: acc.inactive + c.inactive,
              total: acc.total + c.total
            }), { registered: 0, unregistered: 0, active: 0, inactive: 0, total: 0 });

            return (
              <div 
                key={spocData.spoc_id}
                style={{
                  background: '#fff',
                  borderRadius: 12,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  overflow: 'hidden'
                }}
              >
                {/* SPOC Header */}
                <div
                  onClick={() => toggleSpoc(spocData.spoc_id)}
                  style={{
                    padding: '16px 20px',
                    background: isExpanded ? '#f3f4f6' : '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: isExpanded ? '2px solid #5a4ff3' : 'none',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: '#333', marginBottom: 4 }}>
                      {spocData.spoc_name}
                    </div>
                    <div style={{ fontSize: 14, color: '#666' }}>
                      {spocData.spoc_email} • {totalCompanies} {totalCompanies === 1 ? 'Company' : 'Companies'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 24, alignItems: 'center', marginRight: 16 }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Total Employees</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#5a4ff3' }}>{spocTotal.total}</div>
                    </div>
                    <div style={{ fontSize: 24, color: '#5a4ff3', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                      ▼
                    </div>
                  </div>
                </div>

                {/* SPOC Details */}
                {isExpanded && (
                  <div style={{ padding: 20 }}>
                    {spocData.companies.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 24, color: '#888' }}>
                        No companies assigned to this SPOC.
                      </div>
                    ) : (
                      <>
                        {/* SPOC Summary */}
                        <div style={{ 
                          background: '#f9fafb', 
                          borderRadius: 8, 
                          padding: 16, 
                          marginBottom: 20,
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                          gap: 16
                        }}>
                          <div>
                            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Registered</div>
                            <div style={{ fontSize: 20, fontWeight: 700, color: '#22c55e' }}>{spocTotal.registered}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Unregistered</div>
                            <div style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b' }}>{spocTotal.unregistered}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Active</div>
                            <div style={{ fontSize: 20, fontWeight: 700, color: '#3b82f6' }}>{spocTotal.active}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Inactive</div>
                            <div style={{ fontSize: 20, fontWeight: 700, color: '#ef4444' }}>{spocTotal.inactive}</div>
                          </div>
                        </div>

                        {/* Companies Table */}
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: '#f3f4f6' }}>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#333', borderBottom: '2px solid #e5e7eb' }}>
                                  Company Name
                                </th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#333', borderBottom: '2px solid #e5e7eb' }}>
                                  Establishment Code
                                </th>
                                <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderBottom: '2px solid #e5e7eb' }}>
                                  Registered
                                </th>
                                <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderBottom: '2px solid #e5e7eb' }}>
                                  Unregistered
                                </th>
                                <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderBottom: '2px solid #e5e7eb' }}>
                                  Active
                                </th>
                                <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderBottom: '2px solid #e5e7eb' }}>
                                  Inactive
                                </th>
                                <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderBottom: '2px solid #e5e7eb' }}>
                                  Total
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {spocData.companies.map((company, idx) => (
                                <tr 
                                  key={company.company_id}
                                  style={{ 
                                    borderBottom: idx < spocData.companies.length - 1 ? '1px solid #e5e7eb' : 'none',
                                    background: idx % 2 === 0 ? '#fff' : '#f9fafb'
                                  }}
                                >
                                  <td style={{ padding: '12px', fontWeight: 600, color: '#333' }}>
                                    {company.company_name}
                                  </td>
                                  <td style={{ padding: '12px', color: '#666', fontFamily: 'monospace' }}>
                                    {company.establishment_code || '-'}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'center', color: '#22c55e', fontWeight: 600 }}>
                                    {company.registered}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'center', color: '#f59e0b', fontWeight: 600 }}>
                                    {company.unregistered}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'center', color: '#3b82f6', fontWeight: 600 }}>
                                    {company.active}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'center', color: '#ef4444', fontWeight: 600 }}>
                                    {company.inactive}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'center', fontWeight: 700, color: '#5a4ff3' }}>
                                    {company.total}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AdminSPOCReport;



